// Map a raw 1PIF item (already JSON.parsed) to one of our Entry shapes.
//
// We support six entry types: login, card, license, identity, note, apiKey.
// Anything we don't recognize is reported as unmapped — the UI shows the
// typeName so we can extend coverage when needed. For "best-effort" types
// (passwords.Password, generic wallet entries) we degrade to a note with the
// raw secureContents preserved in the body.

const TYPE_MAP = {
  'webforms.WebForm': mapLogin,
  'passwords.Password': mapPassword,
  'wallet.financial.CreditCard': mapCard,
  'wallet.computer.License': mapLicense,
  'securenotes.SecureNote': mapNote,
  'identities.Identity': mapIdentity,
};

/**
 * Map one raw item. Returns one of:
 *   { entry }                — successfully mapped, ready to upsert
 *   { trashed: true }        — item is in 1Password's trash (skip)
 *   { unmappedTypeName }     — unknown typeName, surfaced to the user
 * Successfully mapped entries also carry a `hasAttachments` flag so the
 * importer can warn that binary data was left behind.
 */
export function mapItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return { unmappedTypeName: '<non-object>' };
  }
  if (isTrashed(raw)) return { trashed: true };

  const typeName = raw.typeName;
  const fn = TYPE_MAP[typeName];
  if (!fn) return { unmappedTypeName: typeName ?? '<missing>' };

  const entry = fn(raw);
  if (!entry) return { unmappedTypeName: typeName };
  return {
    entry: withTimestamps(entry, raw),
    onepifUuid: typeof raw.uuid === 'string' ? raw.uuid : null,
    hasAttachments: detectAttachments(raw),
  };
}

function isTrashed(raw) {
  const t = raw.trashed;
  return t === 'Y' || t === true || t === 1;
}

function detectAttachments(raw) {
  const sc = raw.secureContents ?? {};
  if (Array.isArray(sc.attachments) && sc.attachments.length) return true;
  // Some 1Password versions reference attachments inside section fields.
  for (const sec of (sc.sections ?? [])) {
    for (const f of (sec.fields ?? [])) {
      if (f?.k === 'attachment' || f?.t === 'attachment') return true;
    }
  }
  return false;
}

// --- mappers ---

function mapLogin(raw) {
  const sc = raw.secureContents ?? {};
  const fields = Array.isArray(sc.fields) ? sc.fields : [];

  // Pick username/password out of the fields array. 1PIF uses designation +
  // type (T=text, P=password, E=email) so we trust designation first.
  // Every field access is optional-chained: `fields` is untrusted JSON, and a
  // single null/string element used to throw and abort the whole import run.
  const usernameField = fields.find((f) => f?.designation === 'username')
    ?? fields.find((f) => f?.type === 'E')
    ?? fields.find((f) => f?.type === 'T');
  const passwordField = fields.find((f) => f?.designation === 'password')
    ?? fields.find((f) => f?.type === 'P');

  const username = usernameField?.value ?? '';
  const password = passwordField?.value ?? '';

  const customFields = fields
    .filter((f) => f !== usernameField && f !== passwordField)
    .filter((f) => f?.value)
    .map((f) => ({ name: f.name ?? f.designation ?? '', value: f.value }));

  const url = raw.location
    ?? (Array.isArray(sc.URLs) ? sc.URLs[0]?.url : null)
    ?? '';

  return {
    type: 'login',
    title: raw.title ?? '',
    username,
    password,
    url,
    notes: sc.notesPlain ?? '',
    // Must be `totp` — that is the field name the entry model, the detail view
    // and the editor all read. It was `totpSecret`, which nothing read, so
    // imported 2FA seeds were stored and then silently invisible.
    totp: pickTotp(sc) ?? '',
    customFields: customFields.length ? customFields : undefined,
  };
}

function mapPassword(raw) {
  const sc = raw.secureContents ?? {};
  return {
    type: 'login',
    title: raw.title ?? '',
    username: '',
    password: sc.password ?? '',
    url: raw.location ?? '',
    notes: sc.notesPlain ?? '',
  };
}

function mapCard(raw) {
  const sc = raw.secureContents ?? {};
  return {
    type: 'card',
    title: raw.title ?? '',
    cardholder: sc.cardholder ?? '',
    number: sc.ccnum ?? '',
    expiryMonth: sc.expiry_mm ?? '',
    expiryYear: sc.expiry_yy ?? '',
    cvv: sc.cvv ?? '',
    pin: sc.pin ?? '',
    notes: sc.notesPlain ?? '',
  };
}

function mapLicense(raw) {
  const sc = raw.secureContents ?? {};
  return {
    type: 'license',
    title: raw.title ?? '',
    product: sc.product_version ? `${raw.title ?? ''}`.trim() : (raw.title ?? ''),
    version: sc.product_version ?? '',
    licenseKey: sc.reg_code ?? '',
    email: sc.reg_email ?? '',
    orderNumber: sc.order_number ?? '',
    purchaseDate: sc.order_date ?? '',
    notes: sc.notesPlain ?? '',
  };
}

function mapNote(raw) {
  const sc = raw.secureContents ?? {};
  return {
    type: 'note',
    title: raw.title ?? '',
    body: sc.notesPlain ?? '',
  };
}

function mapIdentity(raw) {
  const sc = raw.secureContents ?? {};
  return {
    type: 'identity',
    title: raw.title ?? '',
    firstName: sc.firstname ?? '',
    lastName: sc.lastname ?? '',
    email: sc.email ?? '',
    phone: sc.defphone ?? sc.homephone ?? '',
    address: joinAddress(sc),
    notes: sc.notesPlain ?? '',
  };
}

// --- helpers ---

function pickTotp(sc) {
  const fields = Array.isArray(sc.fields) ? sc.fields : [];
  const sections = Array.isArray(sc.sections) ? sc.sections : [];
  const isTotp = (f) => /^totp/i.test(f?.n ?? '') || /otpauth:\/\//i.test(f?.v ?? '');
  for (const f of fields) {
    if (isTotp(f)) return f.value ?? f.v ?? null;
  }
  for (const sec of sections) {
    if (Array.isArray(sec?.fields)) {
      for (const f of sec.fields) {
        if (isTotp(f)) return f?.v ?? f?.value ?? null;
      }
    }
  }
  return null;
}

function joinAddress(sc) {
  const parts = [sc.address1, sc.address2, sc.city, sc.state, sc.zip, sc.country]
    .map((s) => (s ?? '').toString().trim())
    .filter(Boolean);
  return parts.join(', ');
}

function withTimestamps(entry, raw) {
  // 1Password timestamps are seconds since epoch; our Entry uses ms.
  const ts = (n) => (typeof n === 'number' && n > 0 ? n * 1000 : undefined);
  return {
    ...entry,
    createdAt: ts(raw.createdAt),
    modifiedAt: ts(raw.updatedAt) ?? ts(raw.createdAt),
  };
}
